import { trainingManagementSagas } from "../sagas/trainingManagementSagas";
import { RequestState, createInitialRequest } from "framework/state/requestState";
import {
  ApiTrainingArticleIdGetRequest,
  ApiTrainingArticlePutRequest,
  ApiTrainingArticlesGetRequest,
  ApiTrainingFeedbackPostRequest,
  ApiTrainingNodeDeleteRequest,
  ApiTrainingNodeGetRequest,
  ApiTrainingNodePutRequest,
  ApiTrainingNodesGetRequest,
  ApiTrainingNodesRootsGetRequest,
  ArticleDto,
  NodeDto,
  NodeVm
} from "api";
import { AppState } from "../../../../setup/appRootReducer";
import produce from "immer";
import { handleRequestActions } from "../../../../framework/state/genericAsyncRequest";
import { RequestStatus } from "framework/state/requestStatus";
import { TrainingActions, TrainingActionType } from "../actions/trainingActions";
import { searchEngineAccessKey } from "configuration/searchEngine";

interface Requests {
  updateArticle: RequestState<ApiTrainingArticlePutRequest>;
  updateNode: RequestState<ApiTrainingNodePutRequest>;
  deleteNode: RequestState<ApiTrainingNodeDeleteRequest>;
  getArticles: RequestState<ApiTrainingArticlesGetRequest>;
  getNodesRoots: RequestState<ApiTrainingNodesRootsGetRequest>;
  getNodes: RequestState<ApiTrainingNodesGetRequest>;
  getNode: RequestState<ApiTrainingNodeGetRequest>;
  getArticleById: RequestState<ApiTrainingArticleIdGetRequest>;
  sendFeedback: RequestState<string, ApiTrainingFeedbackPostRequest>;
}

export interface ManageTrainingManagementState {
  requests: Requests;
  nodes: NodeVm[];
  node: NodeDto | undefined;
  trainingNode: NodeDto | undefined;
  roots: NodeDto[];
  articles: ArticleDto[];
  article: ArticleDto | undefined;
  openArticle: ArticleDto | undefined;
  searchKey: string | undefined;
}
const defaultState: ManageTrainingManagementState = {
  requests: {
    updateArticle: createInitialRequest(),
    updateNode: createInitialRequest(),
    deleteNode: createInitialRequest(),
    getArticles: createInitialRequest(),
    getNodesRoots: createInitialRequest(),
    getNode: createInitialRequest(),
    getArticleById: createInitialRequest(),
    sendFeedback: createInitialRequest(),
    getNodes: createInitialRequest()
  },
  nodes: [],
  roots: [],
  articles: [],
  node: undefined,
  trainingNode: undefined,
  article: undefined,
  openArticle: undefined,
  searchKey: undefined
};

export function manageTrainingManagementReducer(
  state: ManageTrainingManagementState = defaultState,
  action: TrainingActions
): ManageTrainingManagementState {
  state = handleRequestActions(state, "requests", action, [
    {
      actionTypes: trainingManagementSagas.getNodes.actionTypes,
      key: "getNodes"
    },
    {
      actionTypes: trainingManagementSagas.getNode.actionTypes,
      key: "getNode"
    },
    {
      actionTypes: trainingManagementSagas.getNodesRoots.actionTypes,
      key: "getNodesRoots"
    },
    {
      actionTypes: trainingManagementSagas.getArticles.actionTypes,
      key: "getArticles"
    },
    {
      actionTypes: trainingManagementSagas.getArticleById.actionTypes,
      key: "getArticleById"
    },
    {
      actionTypes: trainingManagementSagas.updateArticle.actionTypes,
      key: "updateArticle"
    },
    {
      actionTypes: trainingManagementSagas.updateNode.actionTypes,
      key: "updateNode"
    },
    {
      actionTypes: trainingManagementSagas.deleteNode.actionTypes,
      key: "deleteNode"
    },
    {
      actionTypes: trainingManagementSagas.sendFeedback.actionTypes,
      key: "sendFeedback"
    },
    {
      actionTypes: trainingManagementSagas.getKey.actionTypes,
      key: "getKey"
    }
  ]);
  if (trainingManagementSagas.getNodes.isCompletedAction(action))
    state = produce(state, (draft) => {
      draft.nodes = action.payload;
    });
  if (trainingManagementSagas.getNode.isCompletedAction(action))
    state = produce(state, (draft) => {
      if (action.payload.name === "Training") {
        draft.trainingNode = action.payload;
      } else {
        draft.node = action.payload;
      }
    });
  if (trainingManagementSagas.getNodesRoots.isCompletedAction(action))
    state = produce(state, (draft) => {
      draft.roots = action.payload;
    });
  if (trainingManagementSagas.getArticles.isCompletedAction(action))
    state = produce(state, (draft) => {
      draft.articles = action.payload;
    });
  if (trainingManagementSagas.updateNode.isCompletedAction(action))
    state = produce(state, (draft) => {
      // when I add a node I execute the update to get the path
      draft.nodes = draft.nodes.filter((n) => !action.payload.some((x) => x.id === n.id));
      action.payload.forEach((node) => draft.nodes.push(node));
    });
  if (trainingManagementSagas.updateArticle.isCompletedAction(action))
    state = produce(state, (draft) => {
      draft.articles = [...draft.articles, action.payload];
    });
  if (trainingManagementSagas.deleteNode.isCompletedAction(action))
    state = produce(state, (draft) => {
      draft.nodes = draft.nodes.filter((n) => !action.payload.some((id) => n.id === id));
    });
  if (trainingManagementSagas.getArticleById.isCompletedAction(action))
    state = produce(state, (draft) => {
      draft.article = action.payload;
    });
  if (trainingManagementSagas.getKey.isCompletedAction(action))
    state = produce(state, (draft) => {
      window.localStorage.setItem(searchEngineAccessKey, action.payload);
      draft.searchKey = action.payload;
    });

  switch (action.type) {
    case TrainingActionType.VisibleArticle:
      state = produce(state, (draft) => {
        draft.openArticle = action.article;
      });
      break;
  }
  return state;
}
export const getOpenArticle = (state: AppState): ArticleDto | undefined =>
  state.training.openArticle;

export const getUpdateNodeStatus = (state: AppState): RequestStatus =>
  state.training.requests.updateNode.status;
export const getUpdateArticleStatus = (state: AppState): RequestStatus =>
  state.training.requests.updateArticle.status;
export const getDeleteNodeStatus = (state: AppState): RequestStatus =>
  state.training.requests.deleteNode.status;
export const getNodes = (state: AppState): NodeVm[] => state.training.nodes;
export const getNodesStatus = (state: AppState): RequestStatus =>
  state.training.requests.getNodes.status;
export const getNode = (state: AppState): NodeDto | undefined => state.training.node;
export const getNodeStatus = (state: AppState): RequestStatus =>
  state.training.requests.getNode.status;
export const getTrainingNode = (state: AppState): NodeDto | undefined =>
  state.training.trainingNode;
export const getSearchKey = (state: AppState): string | undefined => state.training.searchKey;
export const getTrainingNodeStatus = (state: AppState): RequestStatus =>
  state.training.requests.getNode.status;
export const getNodesRoots = (state: AppState): NodeDto[] => state.training.roots;
export const getNodesRootsStatus = (state: AppState): RequestStatus =>
  state.training.requests.getNodesRoots.status;
export const getArticles = (state: AppState): ArticleDto[] => state.training.articles;
export const getArticlesStatus = (state: AppState): RequestStatus =>
  state.training.requests.getArticles.status;
export const getArticleById = (state: AppState): ArticleDto | undefined => state.training.article;
export const getArticleByIdStatus = (state: AppState): RequestStatus =>
  state.training.requests.getArticleById.status;
export const getPostFeedbackRequest = (
  state: AppState
): RequestState<string, ApiTrainingFeedbackPostRequest> => state.training.requests.sendFeedback;
